import { cloneElement, type ReactElement, type ReactNode } from "react";

import { Card } from "./Card";
import { Info } from "./Info";
import { Inline } from "./Inline";
import { Spread } from "./Spread";
import { Stack } from "./Stack";

type SectionCardIcon = ReactElement<{ size?: number | string }>;

export type SectionCardProps = {
  description?: string;
  variant?: "filled" | "outlined";
  renderAction?: () => ReactNode;
  renderBadges?: () => ReactNode;
  icon?: SectionCardIcon;
  children?: ReactNode;
  dataTestId?: string;
  info?: string;
} & (
  | {
      title?: string;
      label?: never;
    }
  | {
      title?: never;
      label?: string;
    }
);

export function SectionCard({
  title,
  label,
  description,
  renderAction,
  renderBadges,
  icon,
  children,
  dataTestId,
  info,
  variant = "filled",
}: SectionCardProps): ReactNode {
  return (
    <Card data-testid={dataTestId} variant={variant}>
      {(renderBadges || title || label || icon || renderAction || description) && (
        <Stack gap="sm">
          {renderBadges && <div className="flex w-full flex-wrap gap-space-xs">{renderBadges()}</div>}
          {(label || title || icon || renderAction || info) && (
            <Spread align="start">
              <Inline gap="sm" align="center">
                {icon && cloneElement(icon, { size: 24 })}
                {title && <h3 className="text-heading-3">{title}</h3>}
                {label && <h4 className="text-body-emphasis">{label}</h4>}
                {info && <Info tooltip={info} />}
              </Inline>
              <div className="flex min-h-section-card-action items-center gap-space-sm">{renderAction?.()}</div>
            </Spread>
          )}
          {description && <pre className="font-sans wrap-break-word whitespace-pre-line">{description}</pre>}
        </Stack>
      )}

      {children}
    </Card>
  );
}
